/**
 * @fileoverview Quick-add task label dialog (MOR-696)
 *
 * A lightweight dialog for creating a firm-defined task label without leaving
 * the task side-panel. Mirrors the create form in the Task Labels manager
 * (name / colour) and calls the same `tasks.labels.create` mutation. On success
 * it hands the new label id back to the caller so the multi-select can
 * immediately apply it to the current task.
 *
 * Admin/owner only — the caller gates the entry point and the mutation enforces
 * the same rule server-side.
 */

import { useState } from "react";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { extractErrorMessage } from "@/utils/errors";

import { ColorSwatchPicker } from "./ColorSwatchPicker";

export function QuickAddLabelDialog({
  orgId,
  open,
  onOpenChange,
  onCreated,
}: {
  orgId: Id<"organizations">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new label id once it has been created. */
  onCreated?: (labelId: Id<"taskLabels">) => void;
}) {
  const create = useConvexMutation(api.tasks.labels.create);

  const [name, setName] = useState("");
  const [color, setColor] = useState<TaskColor>("gray");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName("");
    setColor("gray");
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Label name is required.");
      return;
    }
    setSubmitting(true);
    try {
      const labelId = await create({ orgId, name: trimmed, color });
      toast.success(`Added label "${trimmed}".`);
      onCreated?.(labelId);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not add label."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">New label</DialogTitle>
          <DialogDescription className="text-xs">
            Add a tag for your firm. Manage all of them on the Task Labels page.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Name
            </Label>
            <Input
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void submit();
                }
              }}
              placeholder="Urgent, Waiting on client…"
              className="h-8 text-xs"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Colour
            </Label>
            <ColorSwatchPicker value={color} onChange={setColor} />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            size="sm"
            className="gap-1.5 text-xs"
          >
            {submitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Plus className="size-3.5" />
            )}
            Add label
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
